/**
 * Activity Composable
 * 管理活動列表和詳情
 * 原始來源: cuora-module/src/machine/activity/activity.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/activity/activity.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useFetch, useNuxtApp, useRuntimeConfig } from '#app'
import { useActivityStore } from '@/stores/activity/activity'
import { useMainStore } from '@/stores/main'
import { useNavbarStore } from '@/stores/ui/navbar'
import { activityApi } from '@/api'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { storage } from '~/utils/storage'

export const useActivity = (name) => {
  const { $request } = useNuxtApp()
  const { apiUrl, clientId, appSecret, intranet } = useRuntimeConfig().public
  const { getUserInfo } = useUserInfo()
  const store = useActivityStore()
  const mainStore = useMainStore()
  const { isLoggedIn } = useAuthToken()

  const {
    tick,
    specifyActivities,
    activitiesDetailList,
    loggedInActivityList,
    publicActivityList,
    activityList,
    promotionList,
    activityMap,
    tournamentList,
    refs,
    siteConfig,
    isPlaying,
    loading,
    refreshTimer,
    activeInstances,
    friendsActivityDetail,
    hasFriendActivity,
  } = storeToRefs(store)

  // 同步登入狀態到 store
  watch(
    isLoggedIn,
    (value) => {
      store.setIsLoggedIn(value)
    },
    { immediate: true }
  )

  const uidSite = computed(() => getUserInfo.value?.uid_site || '')

  const fetchList = () => {
    // 未登入時不執行，使用 publicActivityList（由 useFetch SSR 快取）
    if (!isLoggedIn.value) {
      return Promise.resolve()
    }

    // 如果已有請求進行中，直接返回該 Promise
    if (store.fetchPromise) {
      return store.fetchPromise
    }

    store.setLoading(true)

    store.fetchPromise = (async () => {
      try {
        const [activityRes, promotionRes] = await Promise.all([
          $request.send(activityApi.getActivityList({ appSecret })),
          $request.send(activityApi.getPromotionList({ appSecret })),
        ])

        const activities = activityRes.data || []
        const promotions = promotionRes || []

        // 處理活動列表
        setActivityList(activities)

        // 設定促銷列表
        store.setPromotionList(promotions)

        // 過濾錦標賽列表
        setTournamentList(activities)

        // 載入特定活動的詳情
        await fetchActivitiesDetail()

        return { activities, promotions }
      } catch (err) {
        console.error('[useActivity] fetchList error:', err)
        throw err
      } finally {
        store.setLoading(false)
        store.fetchPromise = null
      }
    })()

    return store.fetchPromise
  }

  // 根據環境決定 API base URL
  const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

  // 從 activityApi 取得 API 配置
  const allActivitiesApiConfig = activityApi.getAllActivityList({ appSecret })
  const allActivitiesApiPath = allActivitiesApiConfig.url.replace(':clientId', clientId)

  // 使用 useFetch 自動執行（SSR 友善）- 取得所有活動列表
  useFetch(`${baseUrl}/${allActivitiesApiPath}`, {
    key: 'all-activities',
    dedupe: 'defer',
    query: allActivitiesApiConfig.params,
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    onRequest() {
      // console.log('🚀 [useActivity] fetchAllActivities 發送新請求', {
      //   isServer: import.meta.server,
      //   isClient: import.meta.client,
      // })
    },
    onResponse({ response }) {
      // console.log('✅ [useActivity] fetchAllActivities 收到回應')
      const activities = response._data?.data || []
      store.setPublicActivityList(activities)
    },
    onResponseError({ error: err }) {
      console.error('[useActivity] fetchAllActivities API error:', err)
      store.setPublicActivityList([])
    },
  })

  const setActivityList = (activities) => {
    // 從 localStorage 讀取未讀狀態
    const unreadList = JSON.parse(localStorage.getItem(`${uidSite.value}_unread`)) || {}
    // 不需要標記為未讀的活動類型
    const noReadActivity = [2, 4, 13, 20, 90, 91, 92]

    const listWithUnread = activities.map((activity) => {
      if (noReadActivity.includes(activity.activityType)) {
        // 不需要標記為未讀的活動類型
        activity.unread = false
      } else if (unreadList[activity.activityNo] === undefined) {
        // 預設標記為未讀
        activity.unread = true
      } else {
        // 使用 localStorage 的未讀狀態
        activity.unread = unreadList[activity.activityNo]
      }

      return activity
    })

    store.setLoggedInActivityList(listWithUnread)

    // 創建 activityMap
    const map = activities.reduce((acc, activity) => {
      acc[activity.activityNo] = activity
      return acc
    }, {})

    store.setActivityMap(map)
  }

  const setTournamentList = (activities) => {
    const tournaments = activities.filter((activity) => activity.activityType === 9)
    store.setTournamentList(tournaments)
  }

  const fetchActivitiesDetail = async () => {
    if (!isLoggedIn.value) {
      return
    }

    // 過濾出需要載入詳情的活動
    const needDetailActivities = loggedInActivityList.value.filter((activity) => specifyActivities.value.includes(activity.activityType))

    if (needDetailActivities.length === 0) {
      store.setActivitiesDetailList([])
      return
    }

    try {
      const activityNos = needDetailActivities.map((a) => a.activityNo)
      const response = await Promise.all(
        activityNos.map((activityNo) =>
          $request.send(
            activityApi.getActivityDetail({
              activityNo,
              appSecret,
            })
          )
        )
      )

      // 建立舊資料 Map
      const oldDataMap = new Map(activitiesDetailList.value.map((d) => [d?.activityNo, d]))

      // 處理新資料，空物件則用舊資料
      // 預防頻繁呼叫回傳空物件的情況 response: { data: {} }
      const validDetails = response
        .map((item, index) => {
          const newData = item.data
          const isEmptyData = !newData || Object.keys(newData).length === 0
          return isEmptyData ? oldDataMap.get(activityNos[index]) : newData
        })
        .filter(Boolean)

      store.setLoading(false)
      store.setActivitiesDetailList(validDetails)
    } catch (error) {
      console.error('[useActivity] fetchActivitiesDetail error:', error)
      store.setActivitiesDetailList([])
    }
  }

  const setUnread = (activityNo, unread = false) => {
    // 更新活動未讀狀態（僅限已登入用戶）
    const activity = loggedInActivityList.value.find((a) => a.activityNo === activityNo)
    if (activity) {
      activity.unread = unread
    }

    // 儲存到 localStorage
    const unreadList = JSON.parse(localStorage.getItem(`${uidSite.value}_unread`)) || {}
    unreadList[activityNo] = unread
    localStorage.setItem(`${uidSite.value}_unread`, JSON.stringify(unreadList))
  }

  const refresh = async () => {
    await fetchList()
  }

  const resetActivities = () => {
    store.setIsPlaying(true)
  }

  const leaveGame = () => {
    store.setIsPlaying(false)
  }

  const startAutoRefresh = () => {
    // SSR 環境跳過
    if (!import.meta.client) {
      return
    }

    // 每次呼叫都執行一次 fetchList 確保資料是最新的
    fetchList()

    // 單例模式：如果已有 timer 運行，則不再創建新的
    if (refreshTimer.value) {
      return
    }

    const timer = setInterval(async () => {
      await fetchList()
    }, tick.value * 10)

    store.setRefreshTimer(timer)
  }

  const stopAutoRefresh = () => {
    // SSR 環境跳過
    if (!import.meta.client) {
      return
    }

    // 檢查是否還有其他組件實例在使用
    const instanceCount = activeInstances.value.size
    if (instanceCount > 0) {
      const instanceIds = Array.from(activeInstances.value).join(', ')
      console.log(`[useActivity] Still ${instanceCount} active instance(s): [${instanceIds}], keeping timer running`)
      return
    }

    // 只有當沒有活躍實例時才清除 timer
    if (refreshTimer.value) {
      console.log('[useActivity] No active instances, stopping timer')
      clearInterval(refreshTimer.value)
      store.setRefreshTimer(null)
    }
  }

  // 好友活動相關
  const friendActivity = computed(() => store.getFriendActivity)

  const getFriendsActivityDetail = computed(() => friendsActivityDetail.value)

  // Navbar 設定過濾（直接從 navbarStore 取得並根據 activityList 動態過濾）
  const navbarStore = useNavbarStore()
  const navbarSettings = computed(() => {
    const navbarData = navbarStore.navbar || []
    const activityListData = activityList.value || []

    if (activityListData.length > 0) {
      return navbarData.filter((item) => {
        if (item.type === 'ACTIVITY') {
          return activityListData.find((activity) => activity.activityNo === item.activityNo)
        } else if (item.type === 'PROMOTIONS') {
          return activityListData.find((promotion) => promotion.activityNo === item.promotionsNo)
        } else {
          return true
        }
      })
    } else {
      return navbarData
    }
  })

  const fetchFriendsActivityDetail = async (activityNo) => {
    if (!activityNo) {
      console.warn('[useActivity] fetchFriendsActivityDetail: activityNo is required')
      return null
    }

    try {
      const response = await $request.send(
        activityApi.getActivityDetail({
          activityNo,
          appSecret,
        })
      )
      store.setFriendsActivityDetail(response.data)
      return response.data
    } catch (error) {
      console.error('[useActivity] fetchFriendsActivityDetail error:', error)
      store.setFriendsActivityDetail(null)
      return null
    }
  }

  // 自動啟動定時器
  onMounted(() => {
    // 註冊具名實例
    store.registerInstance(name)
    startAutoRefresh()

    // 存儲 publicActivityList 到 localStorage
    if (Array.isArray(publicActivityList.value)) {
      storage.set('public-activity-list', publicActivityList.value)
    }
  })

  // 清理定時器
  onUnmounted(() => {
    // 取消註冊具名實例
    store.unregisterInstance(name)
    stopAutoRefresh()
  })

  return {
    // State
    tick,
    specifyActivities,
    activitiesDetailList,
    activityList,
    publicActivityList,
    promotionList,
    activityMap,
    tournamentList,
    refs,
    siteConfig,
    isPlaying,
    loading,
    uidSite,

    // 好友活動相關
    friendActivity,
    getFriendsActivityDetail,
    hasFriendActivity,

    // Navbar 設定
    navbarSettings,

    // Methods
    fetchList,
    setUnread,
    refresh,
    resetActivities,
    leaveGame,
    stopAutoRefresh,
    fetchFriendsActivityDetail,
  }
}
